/**
 * AttendanceCalculation — trigger the monthly payroll calculation and
 * review per-worker summary rows.
 *
 * Migrated to live API in Wave 3.2. Reads / writes:
 *   POST /v1/attendance/calculation/jobs       → queues a job
 *   GET  /v1/attendance/calculation/status/{job_id}
 *   GET  /v1/attendance/calculation/results   → { status, results: [] }
 *
 * The backend is still a Phase 3 stub (every call succeeds but
 * returns an empty `results` array). Wiring is ready for when the
 * real engine lands: the result row shape (presentDays / absentDays
 * / etc) is derived per-row from the dict keys the backend returns,
 * so the table re-renders whatever schema the real implementation
 * settles on.
 *
 * Flow:
 *   1. Load last results on mount (GET /results)
 *   2. User picks date range + optional department, clicks Calculate
 *   3. POST /run → get job_id, immediately poll /status/{job_id}
 *   4. On completed status, refetch /results to show rows
 *   5. Banner + status chip display job progress throughout
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, FormControl,
  IconButton, InputLabel, LinearProgress, MenuItem, Paper, Select,
  Snackbar, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  CheckCircleOutlined, FileDownloadOutlined, PlayArrowOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { ApiError, attendanceApi, organizationApi } from '../../../api';
import type {
  CalculationRequest, CalculationResult, CalculationStatus,
} from '../../../api/attendance';
import type { DepartmentNode } from '../../../api/organization';
import { useTranslation } from 'react-i18next';

import DateField from '../../../components/inputs/DateField';
import { translateApiError } from '../../../i18n/translateApiError';
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function monthStart(today = new Date()) { const d = new Date(today); d.setDate(1); return isoDate(d); }
function lastMonth(): [string, string] {
  const d = new Date();
  d.setDate(1);
  const end = new Date(d);
  end.setDate(0);
  d.setMonth(d.getMonth() - 1);
  return [isoDate(d), isoDate(end)];
}

const periodOptions = [
  { value: 'this_week', labelKey: 'attendance:calculation.periodThisWeek' },
  { value: 'last_week', labelKey: 'attendance:calculation.periodLastWeek' },
  { value: 'last_7_days', labelKey: 'attendance:calculation.periodLast7Days' },
  { value: 'this_month', labelKey: 'attendance:calculation.periodThisMonth' },
  { value: 'last_month', labelKey: 'attendance:calculation.periodLastMonth' },
  { value: 'last_30_days', labelKey: 'attendance:calculation.periodLast30Days' },
];

export function periodRange(v: string, today = new Date()): [string, string] | null {
  const iso = isoDate;
  switch (v) {
    case 'this_week': {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7; // Mon=0
      d.setDate(d.getDate() - day);
      return [iso(d), iso(today)];
    }
    case 'last_week': {
      const d = new Date(today);
      const day = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - day - 7);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      return [iso(d), iso(e)];
    }
    case 'last_7_days': {
      const e = new Date(today);
      const s = new Date(today); s.setDate(s.getDate() - 6);
      return [iso(s), iso(e)];
    }
    case 'this_month': return [monthStart(today), iso(today)];
    case 'last_month': return lastMonth();
    case 'last_30_days': {
      const e = new Date(today);
      const s = new Date(today); s.setDate(s.getDate() - 29);
      return [iso(s), iso(e)];
    }
    default: return null;
  }
}

function initials(name: string | null | undefined): string {
  if (!name) return '•';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return `${first}${last}`.toUpperCase() || '•';
}

function pickNum(row: Record<string, unknown>, ...keys: string[]): number | null {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
}

function pickStr(row: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === 'string' && v !== '') return v;
  }
  return '';
}

function rateColor(r: number | null): 'success.main' | 'warning.main' | 'error.main' | 'text.disabled' {
  if (r === null) return 'text.disabled';
  if (r >= 95) return 'success.main';
  if (r >= 85) return 'warning.main';
  return 'error.main';
}

function statusChipColor(s: string | null | undefined): 'info' | 'success' | 'warning' | 'error' | 'default' {
  if (s === 'completed') return 'success';
  if (s === 'running') return 'info';
  if (s === 'queued') return 'warning';
  if (s === 'failed') return 'error';
  return 'default';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DepartmentLite { id: string; name: string }

const AttendanceCalculation: React.FC = () => {
  const { t } = useTranslation();
  const [depts, setDepts] = useState<DepartmentLite[]>([]);
  const [job, setJob] = useState<CalculationStatus | null>(null);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const today = isoDate(new Date());
  const [startDate, setStartDate] = useState(() => monthStart());
  const [endDate, setEndDate] = useState(() => isoDate(new Date()));
  const [quickPeriod, setQuickPeriod] = useState('this_month');
  const [deptFilter, setDeptFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [workerSearch, setWorkerSearch] = useState('');
  const [locFilter, setLocFilter] = useState('all');
  const [locations, setLocations] = useState<{id: string; name: string}[]>([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ---------- data ----------
  const fetchLastResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await attendanceApi.calculationApi.results();
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('common:feedback.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchLastResults(); }, [fetchLastResults]);

  useEffect(() => {
    organizationApi.departmentsApi
      .list()
      .then((r) => setDepts(r.map((d: DepartmentNode) => ({ id: d.id, name: d.name }))))
      .catch(() => setDepts([]));
    organizationApi.locationsApi
      .list()
      .then((r: any[]) => setLocations(r.map((l) => ({ id: l.id, name: l.name }))))
      .catch(() => setLocations([]));
  }, []);

  // ---------- period ----------
  const handleQuickPeriod = (v: string) => {
    setQuickPeriod(v);
    const r = periodRange(v);
    if (r) {
      setStartDate(r[0]);
      setEndDate(r[1]);
    }
  };

  const handleDateChange = (field: 'start' | 'end', value: string) => {
    if (field === 'start') setStartDate(value);
    else setEndDate(value);
    setQuickPeriod('custom');
  };

  // ---------- calculate ----------
  const handleRun = async () => {
    if (!startDate || !endDate || running) return;
    setRunning(true);
    setError(null);
    try {
      const payload: CalculationRequest = {
        start_date: startDate,
        end_date: endDate,
        department_id: deptFilter === 'all' ? null : deptFilter,
      };
      const queued = await attendanceApi.calculationApi.run(payload);
      setJob(queued);
      setToast(t('attendance:calculation.jobQueued', { jobId: queued.job_id }));
      // Poll status until completed/failed. Backend stub flips straight to
      // completed, but this polls in real-world deploys too.
      for (let i = 0; i < 20; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const s = await attendanceApi.calculationApi.status(queued.job_id);
        setJob(s);
        if (s.status === 'completed' || s.status === 'failed') break;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 800));
      }
      await fetchLastResults();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('attendance:calculation.calculationFailed'));
    } finally {
      setRunning(false);
    }
  };

  // ---------- render ----------
  const rows = useMemo(
    () => (result?.results ?? []) as Record<string, unknown>[],
    [result],
  );

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (locFilter !== 'all' && (r.location || '') !== locFilter) return false;
      if (workerSearch.trim()) {
        const q = workerSearch.toLowerCase();
        const name = ((r.worker_name as string) || (r.worker as string) || '').toLowerCase();
        const code = ((r.worker_code as string) || '').toLowerCase();
        if (!name.includes(q) && !code.includes(q)) return false;
      }
      return true;
    });
  }, [rows, statusFilter, locFilter, workerSearch]);

  const paginated = useMemo(
    () => filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRows, page, rowsPerPage],
  );

  const statusLabel = job
    ? `${job.status}${job.progress != null ? ` · ${job.progress}%` : ''}`
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', color: 'text.primary' }}>{t('attendance:calculation.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('attendance:calculation.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <DateField value={startDate} maxDate={today} onChange={(e) => handleDateChange('start', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Typography variant="caption" color="text.secondary" sx={{ px: 0.25 }}>–</Typography>
          <DateField value={endDate} maxDate={today} onChange={(e) => handleDateChange('end', e.target.value)} size="small"
            sx={{ width: 135, '& .MuiInputBase-root': { height: 32, fontSize: '0.8rem' } }} />
          <Select value={quickPeriod} onChange={(e) => handleQuickPeriod(e.target.value)} size="small"
            sx={{ minWidth: 120, height: 32, fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5, fontWeight: 600 } }}>
            {periodOptions.map((o) => <MenuItem key={o.value} value={o.value}>{t(o.labelKey)}</MenuItem>)}
            <MenuItem value="custom">{t('attendance:calculation.custom')}</MenuItem>
          </Select>
          <Tooltip title={t('attendance:calculation.refreshResults')}>
            <IconButton size="small" onClick={fetchLastResults} disabled={loading || running}
              sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}>
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button variant="contained" size="small" startIcon={<PlayArrowOutlined />}
            onClick={handleRun} disabled={running || !startDate || !endDate}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            {running ? t('attendance:calculation.calculating') : t('attendance:calculation.calculate')}
          </Button>
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlined />}
            onClick={() => setToast(t('attendance:calculation.exportHint'))}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}>
            {t('attendance:calculation.exportLabel')}
          </Button>
        </Stack>
      </Box>

      {/* Job status */}
      <Box sx={{ width: '100%', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        {job ? (
          <>
            <Chip size="small" label={statusLabel} color={statusChipColor(job.status)} variant="outlined"
              sx={{ borderRadius: 1, height: 22, fontSize: '0.7rem' }} />
            <Typography variant="caption" color="text.secondary">
              {t('attendance:calculation.jobIdPrefix', { jobId: job.job_id })} · {t('attendance:calculation.rowCount', { count: filteredRows.length })}{filteredRows.length !== rows.length ? ` ${t('attendance:calculation.totalRows', { total: rows.length })}` : ''}
            </Typography>
          </>
        ) : result ? (
          <>
            <CheckCircleOutlined sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography variant="caption" color="text.secondary">
              {t('attendance:calculation.lastResults')} · {t('attendance:calculation.rowCount', { count: filteredRows.length })}{filteredRows.length !== rows.length ? ` ${t('attendance:calculation.totalRows', { total: rows.length })}` : ''}
            </Typography>
          </>
        ) : null}
        {running && <LinearProgress sx={{ flex: 1, height: 4, borderRadius: 2 }} />}
      </Box>

      {/* Filters + Pagination */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>{t('attendance:calculation.locationLabel')}</InputLabel>
          <Select value={locFilter} label={t('attendance:calculation.locationLabel')} onChange={(e) => { setLocFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('attendance:calculation.all')}</MenuItem>
            {locations.map((l) => <MenuItem key={l.id} value={l.name}>{l.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('attendance:calculation.departmentLabel')}</InputLabel>
          <Select value={deptFilter} label={t('attendance:calculation.departmentLabel')} onChange={(e) => setDeptFilter(e.target.value)}>
            <MenuItem value="all">{t('attendance:calculation.all')}</MenuItem>
            {depts.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>{t('attendance:calculation.statusLabel')}</InputLabel>
          <Select value={statusFilter} label={t('attendance:calculation.statusLabel')} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <MenuItem value="all">{t('attendance:calculation.all')}</MenuItem>
            <MenuItem value="normal">{t('attendance:calculation.statusNormal')}</MenuItem>
            <MenuItem value="late">{t('attendance:calculation.statusLate')}</MenuItem>
            <MenuItem value="early_leave">{t('attendance:calculation.statusEarlyLeave')}</MenuItem>
            <MenuItem value="absent">{t('attendance:calculation.statusAbsent')}</MenuItem>
            <MenuItem value="leave">{t('attendance:calculation.statusLeave')}</MenuItem>
            <MenuItem value="holiday">{t('attendance:calculation.statusHoliday')}</MenuItem>
            <MenuItem value="rest">{t('attendance:calculation.statusRest')}</MenuItem>
            <MenuItem value="incomplete">{t('attendance:calculation.statusIncomplete')}</MenuItem>
            <MenuItem value="pending_review">{t('attendance:calculation.statusPendingReview')}</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" label={t('attendance:calculation.searchWorker')} value={workerSearch}
          onChange={(e) => { setWorkerSearch(e.target.value); setPage(0); }}
          sx={{ width: 200 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredRows.length}
          rowsPerPage={rowsPerPage} page={page}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 }, '.MuiTablePagination-displayedRows': { m: 0 } }} />
      </Box>

      {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('attendance:calculation.colWorker')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colWorkDays')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colPresent')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colAbsent')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colLate')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colOT')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colLeave')}</TableCell>
              <TableCell align="center">{t('attendance:calculation.colAttendanceRate')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                  <Typography variant="body2">
                    {t('attendance:calculation.noResults')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginated.map((r, i) => {
                const name = pickStr(r, 'worker_name', 'worker');
                const code = pickStr(r, 'worker_code', 'workerId');
                const dept = pickStr(r, 'department');
                const workDays = pickNum(r, 'work_days', 'workDays');
                const present = pickNum(r, 'present_days', 'presentDays');
                const absent = pickNum(r, 'absent_days', 'absentDays');
                const late = pickNum(r, 'late_days', 'lateDays');
                const ot = pickNum(r, 'overtime_hours', 'overtimeHours');
                const leave = pickNum(r, 'leave_hours', 'leaveHours');
                const rate = pickNum(r, 'attendance_rate', 'attendanceRate');
                return (
                  <TableRow key={pickStr(r, 'id') || `${i}`} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                          {initials(name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{name || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{[code, dept].filter(Boolean).join(' · ') || '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{workDays ?? '—'}</TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: present != null && present > 0 ? 'success.main' : 'text.secondary' }}>
                        {present ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: absent != null && absent > 0 ? 'error.main' : 'text.secondary' }}>
                        {absent ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: late != null && late > 0 ? 'warning.main' : 'text.secondary' }}>
                        {late ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{ot ?? '—'}</TableCell>
                    <TableCell align="center">{leave ?? '—'}</TableCell>
                    <TableCell align="center">
                      {rate == null ? '—' : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={rate}
                            sx={{ flex: 1, height: 6, borderRadius: 8, bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': { bgcolor: rateColor(rate), borderRadius: 8 } }} />
                          <Typography variant="caption" sx={{ fontWeight: 600, color: rateColor(rate), minWidth: 40 }}>
                            {rate}%
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AttendanceCalculation;
